import { useCallback, useReducer, useRef } from 'react'

import {
    SECTION_ACTIONS,
    SECTION_STATES,
    canSubmitSectionMutation,
    createSectionState,
    normalizeSectionError,
    sectionStateReducer,
} from './sectionState'

const tupleParts = result => {
    if (!Array.isArray(result)) return { error: null, response: result }
    return { error: result[0], response: result[1] }
}

export const responsePayload = response =>
    response && Object.prototype.hasOwnProperty.call(response, 'data') ? response.data : response

export const createPendingGuard = () => {
    let pending = false
    return {
        isPending: () => pending,
        run: async operation => {
            if (pending) return { ignored: true }
            pending = true
            try {
                return await operation()
            } finally {
                pending = false
            }
        },
    }
}

export const useSectionInteraction = (
    initialConfirmedData = null,
    initialDraft = null,
    onMutationSuccess = null,
) => {
    const [state, dispatch] = useReducer(
        sectionStateReducer,
        createSectionState(initialConfirmedData, initialDraft),
    )
    const mutationGuard = useRef(null)
    if (!mutationGuard.current) mutationGuard.current = createPendingGuard()

    const setDraft = useCallback(draft => dispatch({ type: SECTION_ACTIONS.SET_DRAFT, draft }), [])

    const load = useCallback(async loadSection => {
        dispatch({ type: SECTION_ACTIONS.LOAD_START })
        try {
            const result = await loadSection()
            const { error, response } = tupleParts(result)
            if (error) throw error
            const data = responsePayload(response)
            dispatch({ type: SECTION_ACTIONS.LOAD_SUCCESS, data })
            return { ok: true, data }
        } catch (error) {
            dispatch({ type: SECTION_ACTIONS.LOAD_FAILURE, error })
            return { ok: false, error }
        }
    }, [])

    const refresh = useCallback(async refreshSection => {
        dispatch({ type: SECTION_ACTIONS.REFRESH_START })
        try {
            const result = await refreshSection()
            const { error, response } = tupleParts(result)
            if (error) throw error
            const data = responsePayload(response)
            dispatch({ type: SECTION_ACTIONS.REFRESH_SUCCESS, data })
            return { ok: true, data }
        } catch (error) {
            dispatch({ type: SECTION_ACTIONS.REFRESH_FAILURE, error })
            return { ok: false, error }
        }
    }, [])

    const submit = useCallback(options => {
        if (!canSubmitSectionMutation(state) || mutationGuard.current.isPending()) {
            return Promise.resolve({ ignored: true })
        }
        const {
            mutation,
            refresh: refreshSection,
            draft,
            mutationKey,
            successMessage,
            retainDraft = false,
        } = options
        if (typeof mutation !== 'function' || typeof refreshSection !== 'function') {
            return Promise.reject(new TypeError('Mutation and refresh functions are required'))
        }

        return mutationGuard.current.run(async () => {
            dispatch({ type: SECTION_ACTIONS.MUTATION_START, draft, mutationKey })
            let mutationResult
            try {
                mutationResult = await mutation()
            } catch (error) {
                mutationResult = [error, null]
            }
            const mutationParts = tupleParts(mutationResult)
            if (mutationParts.error) {
                const normalized = normalizeSectionError(mutationParts.error)
                if (normalized.code === 'STALE_VERSION' && normalized.current) {
                    dispatch({
                        type: SECTION_ACTIONS.MUTATION_STALE,
                        error: mutationParts.error,
                        current: normalized.current,
                    })
                } else {
                    dispatch({ type: SECTION_ACTIONS.MUTATION_FAILURE, error: mutationParts.error })
                }
                return { ok: false, error: mutationParts.error, stale: normalized.current }
            }

            const committedData = responsePayload(mutationParts.response)
            dispatch({
                type: SECTION_ACTIONS.MUTATION_SUCCESS,
                data: committedData,
                mutationKey,
                successMessage,
                retainDraft,
            })
            if (successMessage && typeof onMutationSuccess === 'function') {
                onMutationSuccess(successMessage)
            }

            try {
                const refreshResult = await refreshSection()
                const refreshParts = tupleParts(refreshResult)
                if (refreshParts.error) throw refreshParts.error
                const refreshedData = responsePayload(refreshParts.response) || committedData
                dispatch({ type: SECTION_ACTIONS.REFRESH_SUCCESS, data: refreshedData })
                return { ok: true, data: committedData, refreshedData }
            } catch (error) {
                dispatch({ type: SECTION_ACTIONS.REFRESH_FAILURE, error })
                return { ok: true, data: committedData, refreshError: error }
            }
        })
    }, [state, onMutationSuccess])

    const acceptStale = useCallback(() => dispatch({ type: SECTION_ACTIONS.ACCEPT_STALE }), [])
    const clearFeedback = useCallback(() => dispatch({ type: SECTION_ACTIONS.CLEAR_FEEDBACK }), [])

    return {
        state,
        setDraft,
        load,
        refresh,
        submit,
        acceptStale,
        clearFeedback,
        canSubmit: canSubmitSectionMutation(state),
        isPending: state.phase === SECTION_STATES.MUTATING || state.phase === SECTION_STATES.REFRESHING,
    }
}
